import { WebBlock } from '../../models';
import { escapeHtml } from './helpers';
import { renderContactForm, renderReservationForm } from './formHelpers';
import { BusinessMetadata } from '../exportHtml';

export function renderBlocks(
  blocks: WebBlock[],
  publicApiBaseUrl?: string,
  businessData?: BusinessMetadata
): string {
  return blocks.map(block => renderBlock(block, publicApiBaseUrl, businessData)).join('\n');
}

export function renderBlock(
  block: WebBlock,
  publicApiBaseUrl?: string,
  businessData?: BusinessMetadata
): string {
  if (block.type === 'hero') {
    const { text = 'Titular Principal', bgColor = '#eef2ff' } = block.props;
    return `
    <section class="hero-block" style="background-color: ${escapeHtml(bgColor)}; padding: var(--theme-hero-pad-y) var(--theme-section-pad-x); text-align: center;">
      <h1 style="font-size: 2.5rem; font-weight: 800; color: var(--theme-text); margin-bottom: 1rem;">${escapeHtml(text)}</h1>
      <p style="font-size: 1.125rem; color: var(--theme-muted); max-width: 600px; margin: 0 auto; line-height: 1.6;">
        Diseño profesional para tu negocio.
      </p>
    </section>`;
  }

  if (block.type === 'text') {
    const { text = 'Texto por defecto', bgColor = '#ffffff' } = block.props;
    return `
    <section class="text-block" style="background-color: ${escapeHtml(bgColor)}; padding: var(--theme-section-pad-y-tight) var(--theme-section-pad-x);">
      <div style="font-size: 1.125rem; color: var(--theme-text); line-height: 1.6; max-width: 800px; margin: 0 auto; white-space: pre-wrap;">${escapeHtml(text)}</div>
    </section>`;
  }

  if (block.type === 'contact') {
    const { title = 'Contáctanos', bgColor = '#f8fafc' } = block.props;
    return `
    <section class="contact-block" style="background-color: ${escapeHtml(bgColor)}; padding: var(--theme-section-pad-y) var(--theme-section-pad-x);">
      <div style="max-width: 600px; margin: 0 auto;">
        <h2 style="font-size: 1.875rem; font-weight: 700; color: var(--theme-text); margin-bottom: 24px; text-align: center;">${escapeHtml(title)}</h2>
${renderContactForm(publicApiBaseUrl)}
      </div>
    </section>`;
  }

  if (block.type === 'reservation') {
    const { title = 'Reserva tu cita', bgColor = '#ffffff' } = block.props;
    return `
    <section class="reservation-block" style="background-color: ${escapeHtml(bgColor)}; padding: var(--theme-section-pad-y) var(--theme-section-pad-x);">
      <div style="max-width: 600px; margin: 0 auto;">
        <h2 style="font-size: 1.875rem; font-weight: 700; color: var(--theme-text); margin-bottom: 24px; text-align: center;">${escapeHtml(title)}</h2>
${renderReservationForm(publicApiBaseUrl)}
      </div>
    </section>`;
  }

  if (block.type === 'business_info') {
    const {
      title = 'Información de Contacto',
      bgColor = '#ffffff',
      showAddress = true,
      showPhone = true,
      showHours = true,
      showEmail = true,
      showMapLink = true
    } = block.props;

    const address = businessData?.address || '';
    const mapUrl = address ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}` : '';

    return `
    <section class="business-info-block" style="background-color: ${escapeHtml(bgColor)}; padding: var(--theme-section-pad-y) var(--theme-section-pad-x);">
      <div style="max-width: 800px; margin: 0 auto; background: var(--theme-surface); border-radius: var(--theme-radius-lg); padding: 40px; border: 1px solid var(--theme-border); box-shadow: var(--theme-shadow);">
        <h2 style="font-size: 1.5rem; font-weight: 700; color: var(--theme-text); margin-bottom: 32px;">${escapeHtml(title)}</h2>
        <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(280px, 1fr)); gap: 32px;">
          <div>
            ${showAddress && address ? `
            <div style="margin-bottom: 24px;">
              <p style="font-size: 0.75rem; font-weight: 800; color: var(--theme-muted); text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 8px;">Dirección</p>
              <p style="font-size: 1rem; color: var(--theme-text); line-height: 1.5;">${escapeHtml(address)}</p>
              ${showMapLink ? `
              <a href="${mapUrl}" target="_blank" style="display: inline-flex; align-items: center; margin-top: 12px; color: var(--theme-accent); text-decoration: none; font-size: 0.875rem; font-weight: 600;">
                Ver en Google Maps →
              </a>` : ''}
            </div>` : ''}
            
            ${showPhone && businessData?.phone ? `
            <div style="margin-bottom: 24px;">
              <p style="font-size: 0.75rem; font-weight: 800; color: var(--theme-muted); text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 8px;">Teléfono</p>
              <a href="tel:${businessData.phone}" style="font-size: 1rem; color: var(--theme-text); text-decoration: none; font-weight: 500;">${escapeHtml(businessData.phone)}</a>
            </div>` : ''}

            ${showEmail && businessData?.email ? `
            <div>
              <p style="font-size: 0.75rem; font-weight: 800; color: var(--theme-muted); text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 8px;">Email</p>
              <a href="mailto:${businessData.email}" style="font-size: 1rem; color: var(--theme-text); text-decoration: none; font-weight: 500;">${escapeHtml(businessData.email)}</a>
            </div>` : ''}
          </div>

          <div>
            ${showHours && businessData?.hours ? `
            <div>
              <p style="font-size: 0.75rem; font-weight: 800; color: var(--theme-muted); text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 8px;">Horario</p>
              <div style="font-size: 1rem; color: var(--theme-text); white-space: pre-wrap; line-height: 1.6;">${escapeHtml(businessData.hours)}</div>
            </div>` : ''}
          </div>
        </div>
      </div>
    </section>`;
  }

  if (block.type === 'gallery') {
    const {
      title = 'Nuestra Galería',
      bgColor = '#ffffff',
      images = [],
      columns = 3
    } = block.props;

    const gridCols = columns;

    const imagesHtml = images.map((img: any) => `
        <div style="aspect-ratio: 1/1; overflow: hidden; border-radius: var(--theme-radius); border: 1px solid var(--theme-border);">
          <img src="${escapeHtml(img.url)}" alt="${escapeHtml(img.alt || '')}" style="width: 100%; height: 100%; object-fit: cover; display: block;" />
        </div>`).join('');

    return `
    <section class="gallery-block" style="background-color: ${escapeHtml(bgColor)}; padding: var(--theme-section-pad-y) var(--theme-section-pad-x);">
      <div style="max-width: 1200px; margin: 0 auto;">
        ${title ? `<h2 style="font-size: 1.875rem; font-weight: 700; color: var(--theme-text); margin-bottom: 40px; text-align: center;">${escapeHtml(title)}</h2>` : ''}
        <div class="gallery-grid" style="display: grid; gap: 16px;">
          ${imagesHtml}
        </div>
      </div>
      <style>
        .gallery-grid { grid-template-columns: repeat(1, 1fr); }
        @media (min-width: 640px) { .gallery-grid { grid-template-columns: repeat(2, 1fr); } }
        @media (min-width: 1024px) { .gallery-grid { grid-template-columns: repeat(${gridCols}, 1fr); } }
      </style>
    </section>`;
  }

  return `<!-- Tipo de bloque no soportado: ${block.type} -->`;
}
